import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { AtlasGridHtmlRendererComponent } from '@ui/components/atlas-grid/renderer/html-renderer.component';
import { GridHeaderSelectComponent } from '@ui/components/atlas-grid/renderer/grid-header-select.component';
import { RowSelectedEvent } from 'ag-grid-community';
import { TranslateService } from '@ngx-translate/core';
import { CheckboxCellComponent } from '@ui/components/atlas-grid/renderer/checkbox-cell-renderer.component';
import { ContactGroup } from '@models/contact/group';
import { User } from '@models/user';
import { AtlasToastService } from '@services/atlas-toast.service';
import { ContactService } from '@services/contact.service';
import { UserService } from '@services/user.service';
import { AtlasGridOptions } from '@ui/components/atlas-grid/altas-grid-options.model';
import { AtlasGridComponent } from '@ui/components/atlas-grid/atlas-grid.component';
import { ButtonGroupRendererComponent } from '@ui/components/atlas-grid/renderer/buttongroup-renderer.component';
import { GridReadyEvent } from 'ag-grid-community';
import { LoaderService } from '@services/loader.service';
import { finalize } from 'rxjs/operators';

// import {faEdit, faEye, faTrash} from '@fortawesome/free-solid-svg-icons';

@Component({
  selector: 'wsb-contacts-groups',
  templateUrl: './groups.component.html',
  styleUrls: ['./groups.component.scss'],
  animations: [
    trigger('gridIsLoaded', [
      state('hide', style({ opacity: 0 })),
      state('show', style({ opacity: 1 })),
      transition('hide => show', animate('150ms', style({ opacity: 1 }))),
      transition('show => hide', animate('150ms', style({ opacity: 0 })))
    ])
  ],
  standalone: false
})
export class ContactsGroupsComponent implements OnInit {
  @Input() page;
  @ViewChild('pageDashboard', { static: true }) $pageDashboard;
  @ViewChild('modalEdit') modalEdit;
  @ViewChild('modalDelete') modalDelete;
  @ViewChild('modalDeleteMultiple') modalDeleteMultiple;
  @ViewChild(AtlasGridComponent) grid: AtlasGridComponent;

  modalEditModal: NgbModalRef;
  modalDeleteModal: NgbModalRef;
  modalDeleteMultipleModal: NgbModalRef;
  editForm: UntypedFormGroup = null;
  user: User | null;
  contactGroupToEdit: ContactGroup | null;
  contactGroupToDelete: ContactGroup | null;

  labelBadge: string;

  bulkOptions;
  isLoaderActive = true;

  tableColumnDefs = null;
  superAccountTable = null;
  subAccountTable = null;
  tableGridOptions = null;
  tableData = null;
  enableSelection = true;
  components = {
    buttonGroupRenderer: ButtonGroupRendererComponent,
    htmlRenderer: AtlasGridHtmlRendererComponent
  };
  tableLoading: boolean;
  paginationPageSize = 10;
  gridAnimationState = 'hide';
  constructor(
    private userService: UserService,
    private contactService: ContactService,
    private modalService: NgbModal,
    private loaderService: LoaderService,
    private formBuilder: UntypedFormBuilder,
    private translateService: TranslateService,
    private atlasToast: AtlasToastService,
    private router: Router
  ) {}

  ngOnInit() {
    this.generateOverlay();
    this.defineColumns();
    if (this.page === 'send') {
      this.bulkOptions = [];
    } else {
      this.bulkOptions = [
        {
          label: 'common_delete',
          onClick: () => {
            this.openModalDeleteMultiple();
          }
        }
      ];
    }
    const that = this;
    this.editForm = this.formBuilder.group({
      name: ['', [Validators.required]],
      description: ['', []]
    });

    this.userService.getAuthenticationState().subscribe(next => (this.user = next));

    if (this.user.account.subaccount) {
      this.tableColumnDefs = this.subAccountTable;
    } else {
      this.tableColumnDefs = this.superAccountTable;
    }

    this.tableGridOptions = AtlasGridOptions.deserialize({
      onGridReady: (event: GridReadyEvent) => {
        this.gridAnimationState = 'show';
      },
      onRowSelected: (event: RowSelectedEvent) => {
        if (event.data.sharingInfo && event.node.isSelected() && this.page !== 'send') {
          event.node.setSelected(false);
        } else {
          this.grid.selectionChange.emit(event);
        }
      },
      pagination: true,
      paginationPageSize: this.paginationPageSize,
      loadFunction: this.reloadList.bind(that),
      bulkOptions: this.bulkOptions,
      noRowsOverlayOptions: {
        message: ''
      }
    });
    // this.reloadList();
  }

  generateOverlay() {
    this.loaderService.resetOverlayHeight();
    this.loaderService.show();
  }

  defineColumns() {
    this.subAccountTable = [
      {
        headerName: '#',
        field: '__sel',
        width: 60,
        headerComponent: GridHeaderSelectComponent,
        cellRenderer: CheckboxCellComponent,
        cellRendererParams: {
          page: this.page
        },
        sortable: false,
        suppressCellFocus: true,
        resizable: false,
        suppressSizeToFit: true
      },
      {
        headerNameTranslate: 'contacts_group_share_title',
        field: 'shared',
        sortable: false,
        suppressCellFocus: true,
        cellRenderer: 'htmlRenderer',
        cellRendererParams: {
          content: params => {
            if (params && params.data) {
              if (params.data.sharingInfo) {
                this.translateService.get('common_shared').subscribe(label => {
                  this.labelBadge = label;
                });
                return '<span class="badge badge-shared">' + this.labelBadge + '</span>';
              } else {
                return '';
              }
            } else {
              return '';
            }
          }
        }
      },
      {
        headerNameTranslate: 'contacts_group_name',
        field: 'name',
        suppressCellFocus: true
      },
      {
        headerNameTranslate: 'common_description',
        field: 'description',
        sortable: false,
        suppressCellFocus: true
      },
      {
        headerNameTranslate: this.page !== 'mail' ? 'common_contacts' : 'common_contacts_with_email',
        field: this.page !== 'mail' ? 'totalContactsCount' : 'emailContactsCount',
        sortable: false,
        suppressCellFocus: true
      },
      {
        headerNameTranslate: 'common_actions',
        suppressCellFocus: true,
        cellRenderer: 'buttonGroupRenderer',
        cellRendererParams: {
          items: [
            {
              onClick: e => this.openModalEdit(e),
              label: 'common_edit',
              hide: group => {
                if (!group) {
                  return false;
                } else {
                  if (group.sharingInfo) {
                    return true;
                  } else {
                    return false;
                  }
                }
              }
              // variant: 'primary',
            },
            {
              onClick: e => this.openModalDelete(e),
              label: 'common_delete',
              hide: group => {
                if (!group) {
                  return false;
                } else {
                  if (group.sharingInfo) {
                    return true;
                  } else {
                    return false;
                  }
                }
              }
              // variant: 'danger',
            },
            {
              onClick: e => this.view(e),
              label: 'groups_table_action_view'
              // variant: 'primary',
            }
          ]
        },
        cellClass: ['button-group', 'text-end'],
        maxWidth: 82
      }
    ];

    this.superAccountTable = [
      {
        headerName: '#',
        field: '__sel',
        width: 60,
        headerComponent: GridHeaderSelectComponent,
        cellRenderer: CheckboxCellComponent,
        sortable: false,
        suppressCellFocus: true,
        resizable: false,
        suppressSizeToFit: true
      },
      {
        headerNameTranslate: 'contacts_group_name',
        field: 'name',
        suppressCellFocus: true
      },
      {
        headerNameTranslate: 'common_description',
        field: 'description',
        sortable: false,
        suppressCellFocus: true
      },
      {
        headerNameTranslate: this.page !== 'mail' ? 'common_contacts' : 'common_contacts_with_email',
        field: this.page !== 'mail' ? 'totalContactsCount' : 'emailContactsCount',
        sortable: false,
        suppressCellFocus: true
      },
      {
        headerNameTranslate: 'common_actions',
        suppressCellFocus: true,
        cellRenderer: 'buttonGroupRenderer',
        hide: true ? this.page === 'send' : false,
        cellRendererParams: {
          items: [
            {
              onClick: e => this.openModalEdit(e),
              label: 'common_edit'
              // variant: 'primary',
            },
            {
              onClick: e => this.openModalDelete(e),
              label: 'common_delete'
              // variant: 'danger',
            },
            {
              onClick: e => this.view(e),
              label: 'groups_table_action_view'
              // variant: 'primary',
            }
          ]
        },
        cellClass: ['button-group', 'text-end'],
        maxWidth: 82
      }
    ];
  }

  reloadList(startRow?, endRow?, sort?) {
    if (!this.isLoaderActive) {
      this.isLoaderActive = true;
      this.loaderService.show();
    }
    return this.contactService
      .getGroups(
        1 + startRow / (endRow - startRow),
        endRow - startRow,
        sort && sort.length ? sort[0].colId : null,
        sort && sort.length ? (sort[0].sort === 'desc' ? 'd' : 'a') : null
      )
      .pipe(
        finalize(() => {
          this.loaderService.hide();
          this.isLoaderActive = false;
        })
      );
  }

  get showHelpAndShop(): boolean {
    return !this.userService.isSubaccount();
  }

  get getUserName(): string {
    if (this.user) {
      return this.user.profile.name;
    }
    return null;
  }

  openModalEdit(data?: any): void {
    this.contactGroupToEdit = data;
    this.modalEditModal = this.modalService.open(this.modalEdit);
    if (data) {
      this.editForm.patchValue(data);
    }
    this.modalEditModal.result.then(
      result => {},
      reason => {}
    );
  }

  openModalDelete(data: any): void {
    this.contactGroupToDelete = data;
    this.modalDeleteModal = this.modalService.open(this.modalDelete);
    this.modalDeleteModal.result.then(
      result => {},
      reason => {}
    );
  }

  modalEditForm(): void {
    if (!this.editForm.valid) {
      return;
    }
    this.editForm.disable();
    const raw = ContactGroup.deserialize(this.editForm.getRawValue());
    if (this.contactGroupToEdit) {
      raw.id = this.contactGroupToEdit.id;
    }
    const op = this.contactGroupToEdit ? this.contactService.editGroup(raw) : this.contactService.addGroup(ContactGroup.deserialize(raw));
    const that = this;
    op.subscribe(
      () => {
        this.editForm.reset();
        this.atlasToast.success('common_success', null);
        this.editForm.reset();
        this.editForm.enable();
        that.grid.refresh();
      },
      () => {
        this.atlasToast.error('common_error', null);
        this.editForm.enable();
        that.grid.refresh();
      },
      () => {}
    );
    this.modalEditModal.close();
  }

  modalDeleteForm(): void {
    const that = this;
    this.contactService.removeGroup(this.contactGroupToDelete).subscribe(
      () => {
        this.atlasToast.success('common_success', null);
        that.grid.refresh();
      },
      () => {
        this.atlasToast.error('common_error', null);
        that.grid.refresh();
      },
      () => {}
    );
    this.modalDeleteModal.close();
  }

  openModalDeleteMultiple(): void {
    this.modalDeleteMultipleModal = this.modalService.open(this.modalDeleteMultiple);
    this.modalDeleteMultipleModal.result.then(
      result => {},
      reason => {}
    );
  }

  modalDeleteMultipleForm(): void {
    const that = this;
    that.grid.getSelectedList().subscribe((x: any) => {
      that.grid.showLoading();
      this.contactService.removeMultipleGroups(x.map(y => y.id)).subscribe(z => {
        that.grid.deselectAll();
        that.grid.refresh();
        this.atlasToast.success('common_success', null);
      });
    });
    this.modalDeleteMultipleModal.close();
  }

  view(data: any): void {
    const contactGroupToView = data;
    this.router.navigate(['/contacts/list'], { queryParams: { group: contactGroupToView.id } });
  }
}
